import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Query keys for dashboard data
export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: (workspaceId: string, userId: string) => 
    [...dashboardKeys.all, 'stats', workspaceId, userId] as const,
  kpis: (workspaceId: string) => 
    [...dashboardKeys.all, 'kpis', workspaceId] as const,
  activities: (workspaceId: string) => 
    [...dashboardKeys.all, 'activities', workspaceId] as const,
};

// Cache configuration: 2 minutes stale, 10 minutes cache
const STALE_TIME = 2 * 60 * 1000; // 2 minutes
const GC_TIME = 10 * 60 * 1000; // 10 minutes

interface StatData {
  current: number;
  previous: number;
}

interface StatsData {
  newTickets: StatData;
  myTickets: StatData;
  waitingTickets: StatData;
  openTickets: StatData;
}

interface KPIsData {
  avgResponseTime: number | null;
  resolutionRate: number;
  ticketsThisWeek: number[];
}

interface Activity {
  id: string;
  type: 'new_ticket' | 'customer_reply' | 'closed' | 'status_change';
  ticketId: string;
  ticketSubject: string;
  customerEmail: string;
  timestamp: string;
  metadata?: {
    newStatus?: string;
    oldStatus?: string;
  };
}

// Hook for StatsCards data
export function useDashboardStats(workspaceId: string | undefined, userId: string | undefined) {
  return useQuery({
    queryKey: dashboardKeys.stats(workspaceId ?? '', userId ?? ''),
    queryFn: async (): Promise<StatsData> => {
      const { data, error } = await (supabase.rpc as CallableFunction)('get_stats_cards', {
        p_workspace_id: workspaceId,
        p_user_id: userId,
      });

      if (error) throw error;
      
      // The RPC returns an array with a single row, extract the first element
      const rawResult = Array.isArray(data) ? data[0] : data;
      
      if (!rawResult) {
        return {
          newTickets: { current: 0, previous: 0 },
          myTickets: { current: 0, previous: 0 },
          waitingTickets: { current: 0, previous: 0 },
          openTickets: { current: 0, previous: 0 },
        };
      }
      
      const result = rawResult as {
        new_tickets_current: number;
        new_tickets_previous: number;
        my_tickets_current: number;
        my_tickets_previous: number;
        waiting_tickets_current: number;
        waiting_tickets_previous: number;
        open_tickets_current: number;
        open_tickets_previous: number;
      };

      return {
        newTickets: { current: result.new_tickets_current, previous: result.new_tickets_previous },
        myTickets: { current: result.my_tickets_current, previous: result.my_tickets_previous },
        waitingTickets: { current: result.waiting_tickets_current, previous: result.waiting_tickets_previous },
        openTickets: { current: result.open_tickets_current, previous: result.open_tickets_previous },
      };
    },
    enabled: !!workspaceId && !!userId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

// Hook for PerformanceKPIs data
export function useDashboardKPIs(workspaceId: string | undefined) {
  return useQuery({
    queryKey: dashboardKeys.kpis(workspaceId ?? ''),
    queryFn: async (): Promise<KPIsData> => {
      const { data, error } = await supabase.rpc('get_performance_kpis' as never, {
        p_workspace_id: workspaceId,
      } as never);

      if (error) throw error;

      const result = data as Array<{
        avg_response_time_hours: number | null;
        resolution_rate: number;
        tickets_per_day: number[];
      }> | null;

      if (!result || result.length === 0) {
        return {
          avgResponseTime: null,
          resolutionRate: 0,
          ticketsThisWeek: [0, 0, 0, 0, 0, 0, 0],
        };
      }

      const row = result[0];
      return {
        avgResponseTime: row.avg_response_time_hours,
        resolutionRate: row.resolution_rate,
        ticketsThisWeek: row.tickets_per_day || [0, 0, 0, 0, 0, 0, 0],
      };
    },
    enabled: !!workspaceId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

// Hook for RecentActivity data
export function useDashboardActivities(workspaceId: string | undefined) {
  return useQuery({
    queryKey: dashboardKeys.activities(workspaceId ?? ''),
    queryFn: async (): Promise<Activity[]> => {
      const { data, error } = await supabase.rpc('get_recent_activities' as never, {
        p_workspace_id: workspaceId,
        p_limit: 5,
      } as never);

      if (error) throw error;

      const results = data as Array<{
        activity_id: string;
        activity_type: string;
        ticket_id: string;
        ticket_subject: string;
        customer_email: string;
        activity_timestamp: string;
        old_status: string | null;
        new_status: string | null;
      }> | null;

      return (results || []).map((row) => ({
        id: row.activity_id,
        type: row.activity_type as Activity['type'],
        ticketId: row.ticket_id,
        ticketSubject: row.ticket_subject,
        customerEmail: row.customer_email,
        timestamp: row.activity_timestamp,
        metadata: row.old_status || row.new_status ? {
          oldStatus: row.old_status || undefined,
          newStatus: row.new_status || undefined,
        } : undefined,
      }));
    },
    enabled: !!workspaceId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}
